import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  IconButton,
  TablePagination,
  Typography,
  Box,
  Chip,
} from "@mui/material";
import { Edit as EditIcon, Delete as DeleteIcon } from "@mui/icons-material";
import { FormattedMessage } from "react-intl";
import { Type } from "../../../../redux/types/types";

interface TypesListProps {
  types: Type[];
  totalTypes: number;
  page: number;
  rowsPerPage: number;
  orderBy: keyof Type | any;
  order: "asc" | "desc";
  onRequestSort: (property: keyof Type) => void;
  onChangePage: (event: unknown, newPage: number) => void;
  onChangeRowsPerPage: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onEditType: (type: Type) => void;
  onDeleteType: (typeId: number) => void;
}

const languages =
  process.env.REACT_APP_DEFAULT_LANGUAGE === "en"
    ? (["en", "uk", "fr"] as const)
    : (["fr", "uk", "en"] as const);
type Language = (typeof languages)[number];

const LocalizedContent: React.FC<{ content: Record<Language, string> }> = ({
  content,
}) => (
  <Box sx={{ display: "flex", flexDirection: "column" }}>
    {languages.map(
      (lang) =>
        content[lang] && (
          <Box
            key={lang}
            sx={{ display: "flex", alignItems: "center", mb: 0.5 }}
          >
            <Chip
              label={lang.toUpperCase()}
              size="small"
              color="primary"
              variant="outlined"
              sx={{ width: 40, mr: 1, flexShrink: 0 }}
            />
            <Typography variant="body2" component="span">
              {content[lang]}
            </Typography>
          </Box>
        )
    )}
  </Box>
);

export default function TypesList({
  types,
  totalTypes,
  page,
  rowsPerPage,
  orderBy,
  order,
  onRequestSort,
  onChangePage,
  onChangeRowsPerPage,
  onEditType,
  onDeleteType,
}: TypesListProps) {
  return (
    <>
      <TableContainer>
        <Table sx={{ minWidth: 650 }} aria-label="type table">
          <TableHead>
            <TableRow>
              {["id", "name", "isDisplayed"].map((column) => (
                <TableCell
                  key={column}
                  sx={{ pl: column !== "id" ? "56px" : undefined }}
                >
                  <TableSortLabel
                    active={orderBy === column}
                    direction={orderBy === column ? order : "asc"}
                    onClick={() => onRequestSort(column as keyof Type)}
                  >
                    <FormattedMessage id={`admin.dashboard.${column}`} />
                  </TableSortLabel>
                </TableCell>
              ))}
              <TableCell>
                <FormattedMessage
                  id="admin.dashboard.image"
                  defaultMessage="Image"
                />
              </TableCell>
              <TableCell>
                <FormattedMessage id="admin.dashboard.subtypes" />
              </TableCell>
              <TableCell>
                <FormattedMessage id="admin.dashboard.actions" />
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {types.map((type: Type) => (
              <TableRow key={type.id}>
                <TableCell>{type.id}</TableCell>
                <TableCell sx={{ pl: 1 }}>
                  <LocalizedContent content={type.name} />
                </TableCell>
                <TableCell>
                  {type.isDisplayed ? (
                    <FormattedMessage id="common.yes" />
                  ) : (
                    <FormattedMessage id="common.no" />
                  )}
                </TableCell>
                <TableCell>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1,
                      flexWrap: "wrap",
                      minWidth: 72,
                      alignItems: "center",
                    }}
                  >
                    {type.image ? (
                      <Box
                        component="img"
                        src={`${process.env.REACT_APP_API_URL}/${type.image}`}
                        alt={`Type ${type.id} preview`}
                        sx={{
                          width: "auto",
                          maxWidth: 72,
                          maxHeight: 48,
                          objectFit: "contain",
                          borderRadius: 1,
                        }}
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        <FormattedMessage id="admin.dashboard.noImagesUploaded" />
                      </Typography>
                    )}
                  </Box>
                </TableCell>
                <TableCell sx={{ pl: 1 }}>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    {type.subtypes.map((subtype) => (
                      <LocalizedContent key={subtype.id} content={subtype.name} />
                    ))}
                  </Box>
                </TableCell>
                <TableCell>
                  <IconButton
                    color="primary"
                    onClick={() => onEditType(type)}
                    size="small"
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    color="secondary"
                    onClick={() => onDeleteType(type.id)}
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={totalTypes}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={onChangePage}
        onRowsPerPageChange={onChangeRowsPerPage}
        labelRowsPerPage={<FormattedMessage id="admin.dashboard.rowsPerPage" />}
      />
    </>
  );
}
